import { ChangeDetectionStrategy, Component, Input, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { AgGridAngular } from 'ag-grid-angular';
import { ColDef, themeQuartz } from 'ag-grid-community';
import * as XLSX from 'xlsx';
import { MaterialModule } from '../../material.module';

interface ExcelSheetView {
  name: string;
  columns: ColDef<Record<string, string | number>>[];
  rows: Record<string, string | number>[];
}

@Component({
  selector: 'app-jk-excel-preview',
  standalone: true,
  imports: [CommonModule, MaterialModule, AgGridAngular],
  templateUrl: './jk-excel-preview.component.html',
  styleUrl: './jk-excel-preview.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class JkExcelPreviewComponent {
  readonly sheets = signal<ExcelSheetView[]>([]);
  readonly activeSheet = signal(0);
  readonly error = signal('');
  readonly sourceEncoding = signal('');
  readonly theme = themeQuartz;
  private currentData: ArrayBuffer | null = null;
  private currentFileName = '';

  @Input()
  set fileName(value: string) {
    this.currentFileName = value;
    this.parse(this.currentData);
  }

  @Input({ required: true })
  set data(value: ArrayBuffer | null) {
    this.currentData = value;
    this.parse(value);
  }

  selectSheet(index: number): void { this.activeSheet.set(index); }

  private parse(data: ArrayBuffer | null): void {
    this.error.set('');
    this.sourceEncoding.set('');
    this.activeSheet.set(0);
    if (!data) { this.sheets.set([]); return; }
    const isCsv = this.currentFileName.toLowerCase().endsWith('.csv');
    try {
      const workbook = isCsv
        ? XLSX.read(this.decodeCsv(data), { type: 'string', cellDates: true })
        : XLSX.read(new Uint8Array(data), { type: 'array', cellDates: true });
      this.sheets.set(workbook.SheetNames.map(name => this.toSheet(name, workbook.Sheets[name])));
    } catch {
      this.sheets.set([]);
      this.error.set(isCsv ? '無法解析 CSV 檔案' : '無法解析 Excel 檔案');
    }
  }

  private decodeCsv(data: ArrayBuffer): string {
    const bytes = new Uint8Array(data);
    try {
      const text = new TextDecoder('utf-8', { fatal: true }).decode(bytes);
      this.sourceEncoding.set('UTF-8');
      return text;
    } catch {
      const text = new TextDecoder('big5', { fatal: true }).decode(bytes);
      this.sourceEncoding.set('Big5');
      return text;
    }
  }

  private toSheet(name: string, worksheet: XLSX.WorkSheet): ExcelSheetView {
    const values = XLSX.utils.sheet_to_json<(string | number)[]>(worksheet, { header: 1, raw: false, defval: '' });
    const columnCount = Math.max(0, ...values.map(row => row.length));
    const columns: ColDef<Record<string, string | number>>[] = [
      { field: '__row', headerName: '#', width: 72, pinned: 'left', filter: false, sortable: false },
      ...Array.from({ length: columnCount }, (_, index): ColDef<Record<string, string | number>> => ({
        field: `c${index}`,
        headerName: this.columnName(index),
        minWidth: 100,
        sortable: true,
        filter: true,
        resizable: true,
      })),
    ];
    const rows = values.map((row, rowIndex) => {
      const record: Record<string, string | number> = { __row: rowIndex + 1 };
      row.forEach((value, columnIndex) => record[`c${columnIndex}`] = value ?? '');
      return record;
    });
    return { name, columns, rows };
  }

  private columnName(index: number): string {
    let value = index + 1;
    let result = '';
    while (value > 0) {
      value -= 1;
      result = String.fromCharCode(65 + (value % 26)) + result;
      value = Math.floor(value / 26);
    }
    return result;
  }
}
